import { Component } from "react";
import { Navigate } from "react-router-dom";
import * as Sentry from "@sentry/browser";
import { isChunkLoadError } from "@/utils/lazyLoad";
import { isWebview, webviewPostMessage } from "@/utils/webviewUtil";

const CHUNK_ERROR_RELOAD_KEY = "chunk_error_reload_count";
const CHUNK_ERROR_RELOAD_TS_KEY = "chunk_error_reload_ts";
const CHUNK_ERROR_MAX_RELOADS = 2;
const CHUNK_ERROR_RESET_INTERVAL = 30000; // 30초

const clearCacheAndReload = () => {
  if (isWebview()) {
    webviewPostMessage("clearWebsiteData", { type: ["cache"] });
    setTimeout(() => window.location.reload(), 300);
  } else {
    window.location.reload();
  }
};

class ErrorBoundary extends Component {
  state = { hasError: false, isChunkError: false, chunkGaveUp: false };

  static getDerivedStateFromError(error) {
    const isChunkError = isChunkLoadError(error);
    return {
      isChunkError,
      hasError: isChunkError || import.meta.env.VITE_APP_ARG === "prd",
    };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error)) {
      if (import.meta.env.VITE_APP_ARG === "prd") {
        Sentry.captureException(error, {
          tags: { type: "chunk_load_error" },
          extra: info,
        });
      }

      const reloadCount = parseInt(sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY) || "0", 10);
      const lastTs = parseInt(sessionStorage.getItem(CHUNK_ERROR_RELOAD_TS_KEY) || "0", 10);
      const now = Date.now();

      if (now - lastTs > CHUNK_ERROR_RESET_INTERVAL) {
        sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, "1");
        sessionStorage.setItem(CHUNK_ERROR_RELOAD_TS_KEY, now.toString());
        clearCacheAndReload();
      } else if (reloadCount < CHUNK_ERROR_MAX_RELOADS) {
        sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, (reloadCount + 1).toString());
        sessionStorage.setItem(CHUNK_ERROR_RELOAD_TS_KEY, now.toString());
        clearCacheAndReload();
      } else {
        sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
        sessionStorage.removeItem(CHUNK_ERROR_RELOAD_TS_KEY);
        this.setState({ chunkGaveUp: true });
      }
      return;
    }

    if (import.meta.env.VITE_APP_ARG === "prd") {
      Sentry.captureException(error, { extra: info });
    }
  }

  render() {
    if (this.state.chunkGaveUp) {
      return <Navigate to="/" replace />;
    }

    if (this.state.isChunkError) {
      return null;
    }

    if (this.state.hasError) {
      return <Navigate to="/" replace />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
